import {WindValues} from '../service/http/station-forecast.service';

/**
 * Der Forecast-Snapshot des Terminal-Rest-Service – Feld-für-Feld-Spiegel von
 * {@code TerminalForecastSnapshot} und {@code ReihenForecast} dort.
 *
 * Der Snapshot enthält die Werte ALLER Regionen in ALLEN Reihen. Der Client holt ihn
 * mit einem einzigen Aufruf ({@code GET forecast/snapshot}) und löst daraus jede Box
 * lokal auf, statt je Box zu fragen.
 *
 * Aufbau: Reihe -> Region -> Wert. Die Schlüssel sind auf beiden Seiten
 * **klein geschrieben** normalisiert, weil die Reihen-Überschriften im Template in
 * Versalien stehen ("HEUTE: ganz"). Die Original-Schreibweise steht in
 * {@link ReihenForecast.reihe}.
 */
export interface ForecastSnapshot {
  /** Zeitpunkt des serverseitigen Refresh, ISO-8601. */
  erstellt: string;
  /** Anzahl der Reihen im Snapshot. */
  reihen: number;
  /** Anzahl der Wettersymbol-Werte über alle Reihen. */
  symbolWerte: number;
  /** Anzahl der Temperatur-Werte über alle Reihen. */
  temperaturWerte: number;
  /** Anzahl der Wind-Werte über alle Reihen. */
  windWerte: number;
  /** Anzahl der Werte je Station (Lokalzeit-Boxen: Heute und Aussichtentage). */
  stationsWerte?: number;
  /** normalisierte Reihen-Überschrift -> Werte dieser Reihe. */
  nachReihe: { [reihe: string]: ReihenForecast };
}

/** Alle Werte einer Karten-Reihe, nach Region geordnet. */
export interface ReihenForecast {
  /** Überschrift in Original-Schreibweise, z.B. 'Heute: Mittag'. */
  reihe: string;
  /** Pfad der Sammlung der Wetterdaten-API. */
  sammlung: string;
  /** Tages-Knoten, z.B. 'tag00'. */
  tag: string;
  /** Zeitfenster, z.B. '12:00-13:00'. */
  zeitspanne: string;
  /** Region -> Wettersymbol-Code. */
  symbole: { [region: string]: number };
  /** Region -> Temperatur in ganzen Grad (Feld luftTemperaturMaximum). */
  temperaturen: { [region: string]: number };
  /** Region -> Wind-Werte, nur für die Karte 'Wind in Bft.'. */
  wind: { [region: string]: WindValues };
  /**
   * Station -> Wettersymbol-Code für die Lokalzeit-Boxen, nur in den Reihen Heute
   * und 1.–3. Aussichtentag gefüllt.
   * Schlüssel ist die Kennung **unverändert** (mit Präfix, nicht klein geschrieben), wie in
   * {@code ImageBox.station}. Optional, weil ein Snapshot von vor dieser Erweiterung sie
   * nicht trägt.
   */
  symboleJeStation?: { [station: string]: number };
  /** Station -> Temperatur in ganzen Grad, wie {@link symboleJeStation}. */
  temperaturenJeStation?: { [station: string]: number };
  /** Station -> Wind-Werte für die Wind-Box der Lokalzeit-Karten. */
  windJeStation?: { [station: string]: WindValues };
}

/**
 * Normalisiert einen Reihen- oder Regionsnamen auf die Schlüsselform des Snapshots.
 * Gegenstück zu {@code RowForecastMapping.normalisiere} bzw. den {@code schluessel}-
 * Methoden der Stations-Tabellen im Backend – muss mit denen gleich bleiben.
 */
export function snapshotKey(name: string | undefined | null): string {
  return (name ?? '').trim().toLowerCase();
}
